/**
 * `/reports` — staff retention reports.
 *
 * Surfaces three read-only canonical actions that previously had NO dashboard
 * home (only the generic `/actions` catalog could call them): the dormant-member
 * sweep, the inactive-member sweep, and the expiring-credits sweep — the same
 * actions that drive the marketing-flow enrolment crons. Each section dispatches
 * its read through the standard chokepoint (`dispatchAction` → `POST
 * /api/dispatch/:action`, as `human:staff`) on mount + when its threshold
 * changes, and renders the rows with a click-through link to each member.
 *
 * Rows are reference-only (member_id + metrics; no name PII), so the member cell
 * links to `/members/:id` where the name + full profile live.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { dispatchAction } from "../lib/dispatch";
import { studioTimeZone } from "../lib/studio-tz";

function fmtDate(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    timeZone: studioTimeZone(),
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function MemberLink({ id }: { readonly id: string }): JSX.Element {
  return (
    <Link to={`/members/${encodeURIComponent(id)}`} className="data-member-link">
      <code>{id.slice(0, 8)}…</code>
    </Link>
  );
}

interface DormantRow {
  readonly member_id: string;
  readonly last_booked_at: string | null;
  readonly last_purchased_at: string | null;
  readonly last_engaged_at: string;
  readonly days_dormant: number;
}
interface InactiveRow {
  readonly member_id: string;
  readonly last_booked_at: string;
  readonly days_inactive: number;
}
interface ExpiringRow {
  readonly member_id: string;
  readonly kind: string;
  readonly slug: string;
  readonly remaining_sessions: number | null;
  readonly expires_at: string | null;
  readonly days_until_expiry: number | null;
}

type AnyRow = DormantRow | InactiveRow | ExpiringRow;

interface ListOutput<Row> {
  readonly rows: ReadonlyArray<Row>;
  readonly next_cursor: string | null;
  readonly count_returned: number;
  readonly limit: number;
}

/**
 * A single retention report. Owns its own threshold control + fetch state, so
 * the three sections load independently (one slow sweep never blocks another).
 */
/** Height presets shared with the member-page card sizer (0 = auto). */
const HEIGHT_PRESETS: ReadonlyArray<{ readonly label: string; readonly value: number }> =
  [
    { label: "Auto", value: 0 },
    { label: "Short", value: 300 },
    { label: "Medium", value: 480 },
    { label: "Tall", value: 720 },
  ];

function ReportSection<Row extends AnyRow>({
  title,
  blurb,
  action,
  thresholdLabel,
  thresholdParam,
  thresholdDefault,
  columns,
}: {
  readonly title: string;
  readonly blurb: string;
  readonly action: string;
  readonly thresholdLabel: string;
  readonly thresholdParam: string;
  readonly thresholdDefault: number;
  readonly columns: ReadonlyArray<{
    readonly head: string;
    readonly cell: (row: Row) => JSX.Element | string;
  }>;
}): JSX.Element {
  // Collapsed by default; the sweep only runs on first expand (lazy), so opening
  // the page never fires all three at once.
  const [expanded, setExpanded] = useState<boolean>(false);
  const [height, setHeight] = useState<number>(0);
  const [threshold, setThreshold] = useState<number>(thresholdDefault);
  const [rows, setRows] = useState<ReadonlyArray<Row> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    const r = await dispatchAction<Record<string, unknown>, ListOutput<Row>>(
      action,
      { [thresholdParam]: threshold, limit: 200 },
    );
    if (r.ok) {
      setRows(r.response.rows);
    } else {
      setError(`${r.message} (${r.code})`);
      setRows(null);
    }
    setLoading(false);
  }, [action, thresholdParam, threshold]);

  // Re-run when the threshold changes WHILE open (not before the first expand).
  useEffect(() => {
    if (expanded) void load();
  }, [expanded, load]);

  const toggle = (): void => {
    setExpanded((v) => !v);
  };

  return (
    <section className="panel report-section">
      <button
        type="button"
        className="report-head"
        onClick={toggle}
        aria-expanded={expanded}
      >
        <span className="report-head-chevron">{expanded ? "▾" : "▸"}</span>
        <span className="report-head-title">{title}</span>
        {rows !== null ? (
          <span className="report-head-count">{rows.length}</span>
        ) : null}
      </button>
      {expanded ? (
        <>
          <p className="member-note">{blurb}</p>
          <div className="report-controls">
            <label>
              {thresholdLabel}{" "}
              <input
                type="number"
                min={1}
                max={730}
                value={threshold}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (Number.isInteger(n) && n > 0) setThreshold(n);
                }}
              />
            </label>
            <label>
              Size{" "}
              <select
                value={height}
                onChange={(e) => setHeight(Number(e.target.value))}
                title="Table height"
              >
                {HEIGHT_PRESETS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="row-action-btn"
              onClick={() => void load()}
              disabled={loading}
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
          {error !== null ? (
            <p className="action-modal-error">{error}</p>
          ) : loading && rows === null ? (
            <p className="empty">Loading…</p>
          ) : rows !== null && rows.length === 0 ? (
            <p className="empty">No members match.</p>
          ) : rows !== null ? (
            <div
              className="table-scroll-x report-table-scroll"
              style={
                height > 0
                  ? { maxHeight: `${height}px`, overflowY: "auto" }
                  : undefined
              }
            >
              <table>
                <thead>
                  <tr>
                    {columns.map((c) => (
                      <th key={c.head}>{c.head}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={`${row.member_id}-${i}`}>
                      {columns.map((c) => (
                        <td key={c.head}>{c.cell(row)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

export function Reports(): JSX.Element {
  return (
    <div className="reports-page">
      <h1 className="page-title">Retention reports</h1>
      <p className="member-note">
        The three retention sweeps that drive the marketing flows — now
        on-screen. Each row links to the member.
      </p>

      <ReportSection<DormantRow>
        title="Dormant members"
        blurb="No booking, purchase, or engagement for the chosen window."
        action="list_dormant_members"
        thresholdLabel="Dormant for at least (days):"
        thresholdParam="dormant_days"
        thresholdDefault={60}
        columns={[
          { head: "Member", cell: (r) => <MemberLink id={r.member_id} /> },
          { head: "Days dormant", cell: (r) => String(r.days_dormant) },
          { head: "Last booked", cell: (r) => fmtDate(r.last_booked_at) },
          { head: "Last purchased", cell: (r) => fmtDate(r.last_purchased_at) },
          { head: "Last engaged", cell: (r) => fmtDate(r.last_engaged_at) },
        ]}
      />

      <ReportSection<InactiveRow>
        title="Inactive members"
        blurb="Have credits/standing but haven't booked in the chosen window."
        action="list_inactive_members"
        thresholdLabel="Inactive for at least (days):"
        thresholdParam="inactive_days"
        thresholdDefault={30}
        columns={[
          { head: "Member", cell: (r) => <MemberLink id={r.member_id} /> },
          { head: "Days inactive", cell: (r) => String(r.days_inactive) },
          { head: "Last booked", cell: (r) => fmtDate(r.last_booked_at) },
        ]}
      />

      <ReportSection<ExpiringRow>
        title="Expiring credits"
        blurb="Packages / memberships with credits expiring soon."
        action="list_expiring_credits"
        thresholdLabel="Expiring within (days):"
        thresholdParam="within_days"
        thresholdDefault={30}
        columns={[
          { head: "Member", cell: (r) => <MemberLink id={r.member_id} /> },
          { head: "Kind", cell: (r) => r.kind },
          { head: "Plan", cell: (r) => <code>{r.slug}</code> },
          {
            head: "Remaining",
            cell: (r) =>
              r.remaining_sessions === null ? "∞" : String(r.remaining_sessions),
          },
          { head: "Expires", cell: (r) => fmtDate(r.expires_at) },
          {
            head: "Days left",
            cell: (r) =>
              r.days_until_expiry === null ? "—" : String(r.days_until_expiry),
          },
        ]}
      />
    </div>
  );
}
